import { h } from 'vue'
import Node from '@/components/Workflows/Pages/Editor/Nodes/Node'
import { deepClone, uuid } from '@/utils/tools'
import {
  CreateWorkflowVariableFieldInput,
  TreeNodeInput,
  WorkflowApprovalRuleInput,
  WorkflowInput,
  WorkflowVariableRule,
} from '@/components/Workflows/Pages/Workflow/workflow'
import { WorkflowVariableFragment } from '@/components/Workflows/Pages/Workflow/graphql/queries'
import NodeConditionRuleComponent from '@/components/Workflows/Pages/Editor/Nodes/NodeConditionRule/NodeConditionRuleComponent.vue'
import NodeApprovals from '@/components/Workflows/Pages/Editor/Nodes/NodeApprovals/NodeApprovals'
import { NodeTypes } from '@/components/Workflows/Pages/Editor/enums'
import { useFragment } from '@/gql'

function ruleToInput(rule: WorkflowVariableRule): CreateWorkflowVariableFieldInput {
  if (rule.operator) {
    const r = useFragment(WorkflowVariableFragment, rule.variable)
    const v: CreateWorkflowVariableFieldInput = {
      id: r.id,
      rule: {
        value: rule.value ?? '',
        operator: rule.operator,
      },
    }
    return v
  }
  throw new Error('Custom rule is not properly created')
}

export default class NodeConditionRule extends Node {
  presetData: boolean // Prevents saving existing data

  compile(): WorkflowInput {
    const base = super.compile()

    const rules: WorkflowVariableRule[] = this.vModel

    const rulesInput: WorkflowApprovalRuleInput = {
      id: this.id,
      parent: this.parentNode?.id, // Should be an id
      children: this.children.map((c) => c.key),
      data: rules.map(ruleToInput),
    }

    base.rules.push(rulesInput)

    return base
  }

  constructor(parent: Node, rules?: WorkflowVariableRule[], options?: Partial<TreeNodeInput>) {
    super({
      name: NodeTypes.NODE_CONDITION,
      key: uuid(),
      canAddNode: false,
      vModel: deepClone(rules) ?? [],
      root: false,
      events: {
        delete(deleteNode: Node) {
          // If we are the last element we reset the tree to have a single approver group
          if (deleteNode.parentNode?.root && deleteNode.parentNode?.childrenLength === 1) {
            deleteNode.parentNode?.addChild(new NodeApprovals(deleteNode, []))
          }

          deleteNode.remove()
        },
      },
      children: [],
      parent,
      component: h(NodeConditionRuleComponent, { trigger: parent.vModel.trigger }),
      ...options,
    })

    this.presetData = this.vModel.length > 0
  }
}
